import {useTranslation} from 'react-i18next';

import {
  Box,
  Checkbox,
  Divider,
  Menu,
  Stack,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  styled,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import {listFiltering} from 'helpers/filters/site';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {type Plan} from 'client';

export function TagSelector({allPlans, setSelectedPlans}: {allPlans: Plan[]; setSelectedPlans: (plans) => void}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const {t} = useTranslation('common');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const tags: string[] = collectTags(allPlans);

  if (!allPlans || !tags.length) {
    return null;
  }

  const numberOfFilteredTags = selectedTags && selectedTags.length;
  const id = open ? 'checkbox-filter-popover' : undefined;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Stack direction={'row'} justifyContent={'center'}>
      <Button
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        color="inherit"
        onClick={handleClick}
        sx={{
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          paddingRight: 0,
        }}
        data-testid="filter-site-menu"
      >
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <Typography variant="body2" color="secondary">
            {t('filterTags')}
          </Typography>
          <Stack direction="row" width={44}>
            {numberOfFilteredTags > 0 && (
              <FilterCounter mr={0.5} variant="body2" width={20} align="center">
                {numberOfFilteredTags}
              </FilterCounter>
            )}
            <ArrowDropDownIcon fontSize="small" color="secondary" />
          </Stack>
        </Stack>
      </Button>
      <Menu id={id} anchorEl={anchorEl} open={open} onClose={handleClose} style={{zIndex: 3000}}>
        <Box sx={{maxHeight: 296, overflowY: 'auto'}}>
          {selectedTags &&
            tags &&
            tags?.map((tag, index) => (
              <MenuItem
                dense
                key={index}
                onClick={() => {
                  const currentTags = listFiltering(selectedTags, String(tag));
                  setSelectedTags(currentTags);
                  setSelectedPlans(getSelectedPlans(listFiltering(selectedTags, String(tag)), allPlans));
                }}
                data-testid={`filter-site-menu-item-${tag}`}
              >
                <ListItemIcon>
                  <Checkbox disableRipple checked={selectedTags.includes(String(tag))} sx={{p: 0}} />
                </ListItemIcon>
                <ListItemText>
                  <Typography fontSize={'small'}>{String(tag).toLocaleUpperCase()}</Typography>
                </ListItemText>
              </MenuItem>
            ))}
        </Box>
        <Divider variant="middle" />
        <Stack direction="row" mx={1} marginTop={1}>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              selectedTags && setSelectedTags([]);
              setSelectedPlans(allPlans);
              handleClose();
            }}
            size="small"
            data-testid={`clear`}
          >
            {t('clear')}
          </Button>
        </Stack>
      </Menu>
    </Stack>
  );
}

const FilterCounter = styled(Typography)(({theme}) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 40,
  color: theme.palette.background.paper,
  width: 20,
}));

const collectTags = (plans: Plan[]): string[] => {
  const result: string[] = plans.flatMap((plan) => plan?.tags ?? []);
  return [...new Set(result)];
};

const getSelectedPlans = (tags: string[], plans: Plan[]): Plan[] => {
  if (tags.length === 0 || !plans) {
    return plans;
  }
  const found = plans.filter((plan) => plan?.tags && areCommonElements(plan?.tags, tags));
  return found.length === 0 ? plans : found;
};

const areCommonElements = (arr1: string[], arr2: string[]): boolean => {
  const arr1Lowercase = arr1.map((element) => element.toLowerCase());
  const arr2Set = new Set(arr2.map((element) => element.toLowerCase()));

  return arr1Lowercase.some((el) => arr2Set.has(el));
};
